import React, { useEffect } from "react";
import { useGeneralContext } from "../../context/AppGeneralContext";

import {
    AccordionTabWrapper,
    AccordionTabSummaryWrapper,
    AccordionTabSummaryContentWrapper,
    AccordionTabSummaryItem,
    AccordionTabSummary,
    AccordionTabSummaryTitleGroup,
    AccordionTabSummaryDescriptionGroup,
    AccordionTabText,
    StyledIcon,
    AccordionTabDetailsWrapper,
} from "./StyledComponents";
import { animate } from "framer-motion";

interface AccordionTabProps {
    title: string;
    description: string;
    mainIconName: string;
    counterValue?: string;
    isActive?: boolean;
    showAccordionTabDetails?: boolean;
    onClickEdit?: () => void;
    children?: JSX.Element | JSX.Element[];
    enableTextPulse?: boolean;
}

const AccordionTab: React.FC<AccordionTabProps> = (props) => {
    const { isIframeMode, isAnonFromTeacherArea } = useGeneralContext();

    // Only pure-anonymous (iframe) users have no sticky app header + close bar above the accordion,
    // so its header pins to the very top. Everyone else — including anonymous-from-teacher-area —
    // has the 60px header + close bar, so the header must pin below them (mirrors ContentWithHeader).
    const hideHeader = isIframeMode && !isAnonFromTeacherArea;

    const onClickEdit = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        props.onClickEdit?.();
    };

    useEffect(() => {
        if (props.enableTextPulse) {
            const controls = animate(
                "#accordion-text-to-animate",
                { scale: [1, 1.5, 1] },
                { duration: 0.4, ease: "easeInOut" }
            );

            return () => {
                controls.stop();
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.counterValue]);

    return (
        <AccordionTabWrapper>
            <AccordionTabSummaryWrapper
                onClick={props.isActive ? undefined : onClickEdit}
                hideHeader={hideHeader}
                isActive={props.isActive}
            >
                <AccordionTabSummaryContentWrapper borderBottom={props.showAccordionTabDetails}>
                    <AccordionTabSummary>
                        <AccordionTabSummaryTitleGroup>
                            <AccordionTabSummaryItem>
                                <StyledIcon
                                    name={props.mainIconName}
                                    $primaryColor={props.isActive}
                                />
                            </AccordionTabSummaryItem>
                            <AccordionTabSummaryItem>
                                <AccordionTabText
                                    primaryColor={props.isActive}
                                    isHighlighted
                                >
                                    {props.title}
                                </AccordionTabText>
                            </AccordionTabSummaryItem>
                        </AccordionTabSummaryTitleGroup>
                        <AccordionTabSummaryDescriptionGroup>
                            <AccordionTabSummaryItem>
                                <AccordionTabText primaryColor={props.isActive}>{props.description}</AccordionTabText>
                            </AccordionTabSummaryItem>
                            {props.counterValue && (
                                <AccordionTabSummaryItem>
                                    <AccordionTabText
                                        // only sets the id when enableTextPulse exists to prevent unintended interactions with other accordions
                                        id={`${props.enableTextPulse ? "accordion-text-to-animate" : ""}`}
                                        primaryColor={props.isActive}
                                    >
                                        {props.counterValue}
                                    </AccordionTabText>
                                </AccordionTabSummaryItem>
                            )}
                        </AccordionTabSummaryDescriptionGroup>
                    </AccordionTabSummary>

                    {props.onClickEdit && (
                        <AccordionTabSummaryItem>
                            <StyledIcon
                                name={"edit-avatar"}
                                onClick={onClickEdit}
                            />
                        </AccordionTabSummaryItem>
                    )}
                </AccordionTabSummaryContentWrapper>
            </AccordionTabSummaryWrapper>
            {props.showAccordionTabDetails && <AccordionTabDetailsWrapper>{props.children}</AccordionTabDetailsWrapper>}
        </AccordionTabWrapper>
    );
};

export default AccordionTab;
